import { memo, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import styled, { keyframes, css } from 'styled-components'
import { CS_m } from 'back-end-api'

import Phone from 'assets/icons/communication/Phone'
import MicrophoneOn from 'assets/icons/communication/MicrophoneOn'
import { useTranslation } from 'services/Translation'
import { ROUTES } from 'Constants'

import { useRingtone } from './useRingtone'

const texts = (getText: (key: string, category: string) => string) => (key: string) =>
  ({
    incomingCall: getText('Incoming call', 'AV_Consult_Incoming'),
    pickUpCall: getText('Pick up call', 'AV_Consult_Incoming'),
    pickUpLater: getText('Pick up later', 'AV_Consult_Incoming'),
    mute: getText('Mute', 'AV_Consult_Incoming'),
    unmute: getText('Unmute', 'AV_Consult_Incoming'),
    patient: getText('Patient', 'AV_Consult_Incoming'),
    appointment: getText('Appointment', 'AV_Consult_Incoming'),
  }[key])

/* ── Animations ── */

const slideUp = keyframes`
  0% { transform: translateY(120%); opacity: 0; }
  100% { transform: translateY(0); opacity: 1; }
`

const iconPulse = keyframes`
  0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(51, 184, 151, 0.55); }
  50% { transform: scale(1.06); box-shadow: 0 0 0 14px rgba(51, 184, 151, 0); }
`

const ringIconShake = keyframes`
  0%, 100% { transform: rotate(0deg); }
  15% { transform: rotate(-12deg); }
  30% { transform: rotate(10deg); }
  45% { transform: rotate(-8deg); }
  60% { transform: rotate(6deg); }
  75% { transform: rotate(-3deg); }
`

/* ── Styled Components ── */

const Container = styled.div`
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 10000;
  width: 340px;
  background: #ffffff;
  border: 1px solid rgba(51, 184, 151, 0.25);
  border-radius: 16px;
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.18), 0 0 0 4px rgba(51, 184, 151, 0.08);
  animation: ${slideUp} 0.35s cubic-bezier(0.22, 1, 0.36, 1);
  font-family: coresanscr55, sans-serif;
  overflow: hidden;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 18px 8px;
`

const IconBadge = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #33b897;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  animation: ${iconPulse} 1.6s ease-in-out infinite;

  svg {
    width: 20px;
    height: 20px;
    animation: ${ringIconShake} 1.6s ease-in-out infinite;
  }
`

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
`

const Title = styled.div`
  font-family: coresanscr65, sans-serif;
  font-size: 0.875rem;
  color: #202336;
  line-height: 1.2;
`

const Subtitle = styled.div`
  font-size: 0.75rem;
  color: rgba(32, 35, 54, 0.55);
  margin-top: 2px;
`

const Body = styled.div`
  padding: 4px 18px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const Row = styled.div`
  font-size: 0.8125rem;
  color: #202336;

  strong {
    font-family: coresanscr65, sans-serif;
    font-weight: normal;
  }
`

const Actions = styled.div`
  display: flex;
  gap: 8px;
  padding: 10px 14px 14px;
  align-items: center;
`

const BaseButton = styled.button<{ variant: 'primary' | 'secondary' | 'icon' }>`
  border: none;
  cursor: pointer;
  font-family: coresanscr65, sans-serif;
  font-size: 0.8125rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: transform 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
  white-space: nowrap;

  &:active {
    transform: translateY(1px);
  }

  ${({ variant }) =>
    variant === 'primary' &&
    css`
      flex: 1;
      padding: 10px 14px;
      border-radius: 10px;
      background: #33b897;
      color: #ffffff;
      box-shadow: 0 4px 12px rgba(51, 184, 151, 0.35);

      &:hover {
        background: #2ea889;
      }
    `}

  ${({ variant }) =>
    variant === 'secondary' &&
    css`
      flex: 1;
      padding: 10px 14px;
      border-radius: 10px;
      background: rgba(32, 35, 54, 0.06);
      color: #202336;

      &:hover {
        background: rgba(32, 35, 54, 0.1);
      }
    `}

  ${({ variant }) =>
    variant === 'icon' &&
    css`
      width: 38px;
      height: 38px;
      border-radius: 10px;
      background: rgba(32, 35, 54, 0.06);
      color: #202336;
      flex-shrink: 0;

      &:hover {
        background: rgba(32, 35, 54, 0.1);
      }

      svg {
        width: 16px;
        height: 16px;
      }
    `}
`

const MuteSlash = styled.span`
  position: absolute;
  display: block;
  width: 22px;
  height: 2px;
  background: #d0021b;
  transform: rotate(-45deg);
  pointer-events: none;
`

const IconButtonWrapper = styled.div`
  position: relative;
  display: inline-flex;
`

/* ── Component ── */

const formatTime = (iso?: string | null): string => {
  if (!iso) return ''
  try {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return ''
    const hh = d.getHours().toString().padStart(2, '0')
    const mm = d.getMinutes().toString().padStart(2, '0')
    return `${hh}:${mm}`
  } catch {
    return ''
  }
}

interface IncomingCallPopupProps {
  appointment: CS_m.ModelsAppointment
  onPickup: () => void
  onPickupLater: () => void
}

const IncomingCallPopup = ({ appointment, onPickup, onPickupLater }: IncomingCallPopupProps) => {
  const { t } = useTranslation(texts)
  const history = useHistory()
  const location = useLocation()

  const [muted, setMuted] = useState(false)
  useRingtone(!muted)

  const registration = appointment?.summary?.registration
  const patientName = registration
    ? `${registration.first_name ?? ''} ${registration.last_name ?? ''}`.trim()
    : ''
  const appointmentTime = formatTime(appointment?.datetime)

  // Determine whether the doctor is already on this patient's summary page.
  // When on a different page we display extra context (patient name + time).
  const onSamePatientPage = useMemo(() => {
    if (!registration?.id) return false
    const target = `${ROUTES.PATIENT}/${registration.id}`
    return location.pathname === target || location.pathname.startsWith(`${target}/`)
  }, [location.pathname, registration?.id])

  const handlePickup = () => {
    // If the doctor is not on this patient's page, navigate there first so the
    // call UI is opened in context.
    if (!onSamePatientPage && registration?.id) {
      history.push(`${ROUTES.PATIENT}/${registration.id}`)
    }
    onPickup()
  }

  const toggleMute = () => setMuted(m => !m)

  return (
    <Container role="dialog" aria-label={t('incomingCall')}>
      <Header>
        <IconBadge>
          <Phone style={undefined} color="#ffffff" />
        </IconBadge>
        <HeaderText>
          <Title>{t('incomingCall')}</Title>
          {onSamePatientPage ? (
            <Subtitle>{patientName || '\u00A0'}</Subtitle>
          ) : (
            <Subtitle>
              {[patientName, appointmentTime].filter(Boolean).join(' · ')}
            </Subtitle>
          )}
        </HeaderText>
      </Header>

      {!onSamePatientPage && (patientName || appointmentTime) && (
        <Body>
          {patientName && (
            <Row>
              <strong>{t('patient')}:</strong> {patientName}
            </Row>
          )}
          {appointmentTime && (
            <Row>
              <strong>{t('appointment')}:</strong> {appointmentTime}
            </Row>
          )}
        </Body>
      )}

      <Actions>
        <BaseButton variant="primary" type="button" onClick={handlePickup}>
          <Phone style={{ width: 14, height: 14 }} color="#ffffff" />
          {t('pickUpCall')}
        </BaseButton>
        <BaseButton variant="secondary" type="button" onClick={onPickupLater}>
          {t('pickUpLater')}
        </BaseButton>
        <IconButtonWrapper title={muted ? t('unmute') : t('mute')}>
          <BaseButton
            variant="icon"
            type="button"
            onClick={toggleMute}
            aria-label={muted ? t('unmute') : t('mute')}
          >
            <MicrophoneOn style={undefined} color="#202336" />
          </BaseButton>
          {muted && <MuteSlash />}
        </IconButtonWrapper>
      </Actions>
    </Container>
  )
}

IncomingCallPopup.whyDidYouRender = true

export default memo(IncomingCallPopup)
